import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Briefcase, Users, ListChecks, TrendingUp, ArrowUpRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, j, a] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/jobs"),
        api.get("/actions"),
      ]);
      setStats(s.data);
      setJobs(j.data);
      setActions(a.data.filter((x) => !x.done).slice(0, 5));
    })();
  }, []);

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-2">Tableau de bord</div>
          <h1 className="font-display text-4xl tracking-tight" data-testid="dashboard-title">
            Bonjour, {user?.name?.split(" ")[0] || "recruteur"}.
          </h1>
        </div>
        <Link to="/app/jobs">
          <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white btn-pill px-6" data-testid="dashboard-new-job-btn">
            + Nouveau poste
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-10">
        <KPI icon={Briefcase} label="Postes ouverts" value={stats?.total_jobs ?? "—"} testid="kpi-jobs" />
        <KPI icon={Users} label="Candidats" value={stats?.total_candidates ?? "—"} testid="kpi-candidates" />
        <KPI icon={ListChecks} label="Actions ouvertes" value={stats?.open_actions ?? "—"} testid="kpi-actions" />
        <KPI icon={TrendingUp} label="En entretien" value={stats?.pipeline?.interview ?? "—"} testid="kpi-interviews" />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl tracking-tight">Postes ouverts</h2>
            <Link to="/app/jobs" className="text-sm text-slate-500 link-underline" data-testid="dashboard-jobs-all">Tout voir →</Link>
          </div>
          <div className="border border-slate-200 bg-white">
            {jobs.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-slate-500 mb-4">Aucun poste créé pour le moment.</p>
                <Link to="/app/jobs"><Button className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="dashboard-empty-create-job">Créer mon premier poste</Button></Link>
              </div>
            ) : (
              jobs.slice(0, 6).map((j) => (
                <Link
                  key={j.id}
                  to={`/app/jobs/${j.id}`}
                  className="flex items-center justify-between p-5 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 transition-colors group"
                  data-testid={`dashboard-job-${j.id}`}
                >
                  <div>
                    <div className="font-medium">{j.title}</div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">{j.candidate_count} candidat(s) · {j.must_have?.length || 0} must-have</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 transition-colors" />
                </Link>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl tracking-tight">Prochaines actions</h2>
            <Link to="/app/actions" className="text-sm text-slate-500 link-underline">Tout →</Link>
          </div>
          <div className="border border-slate-200 bg-white">
            {actions.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Aucune action en attente.</div>
            ) : (
              actions.map((a) => (
                <div key={a.id} className="p-4 border-b last:border-b-0 border-slate-100" data-testid={`dashboard-action-${a.id}`}>
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-mono mb-1">{a.type}</div>
                  <div className="text-sm font-medium">{a.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{a.candidate_name}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-xs tracking-wider uppercase">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className="font-display text-4xl tracking-tight mt-3">{value}</div>
    </div>
  );
}
